import { QueryKey, useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { getCurrencyIdFromLocale } from "@/config/unified-search-config";
import { AutoCompleteResponse } from "@/types/search/location-auto-complete-response";

const fetchLocationResults = async ({ queryKey }: { queryKey: QueryKey }): Promise<AutoCompleteResponse> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
  const [_, currencyId] = queryKey as [unknown, string];

  // Simple API call to get all locations for the filter dropdown
  const apiUrl = `/api/locations?MarketId=1&CurrencyId=${currencyId || "20"}`;

  try {
    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Locations service error: ${response.status}`);
    }

    const data = await response.json();

    // Transform the array response to match our expected format
    // The external API returns an array of locations directly
    return {
      Locations: Array.isArray(data) ? data : []
    };
  } catch (error) {
    throw new Error(
      error instanceof Error
        ? `Failed to load locations: ${error.message}`
        : "Locations service is currently unavailable"
    );
  }
};

const useLocationSearch = () => {
  const locale = useLocale();
  const currencyId = getCurrencyIdFromLocale(locale);

  return useQuery({
    queryKey: ["Location_Search", currencyId],
    queryFn: fetchLocationResults,
    staleTime: Infinity, // Locations don't change frequently
    gcTime: 1000 * 60 * 60 // Cache for 1 hour
  });
};

export default useLocationSearch;